import * as React from 'react';
import { Box, Button, IconButton, MenuItem, Switch, TextField, Typography } from '@material-ui/core';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { FormSectionCard } from '../../components/cards/FormSectionCard';
import { ConfigureTabProps } from './ConfigureTabProps';

const PURPLE = '#6a1b9a';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fieldRow: {
            marginBottom: theme.spacing(2.5),
        },
        label: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: theme.spacing(0.75),
        },
        help: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
        },
        toggleRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            padding: theme.spacing(1.5, 1.75),
            backgroundColor: theme.palette.grey[50],
            marginBottom: theme.spacing(1.75),
        },
        toggleTitle: {
            fontSize: 13,
            fontWeight: 700,
        },
        toggleDesc: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: 2,
            maxWidth: 440,
        },
        ruleTable: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            overflow: 'hidden',
        },
        ruleHeadRow: {
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: theme.spacing(1.25),
            alignItems: 'center',
            padding: theme.spacing(1, 1.5),
            backgroundColor: theme.palette.grey[50],
            fontSize: 10.5,
            fontWeight: 700,
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
        },
        ruleRow: {
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: theme.spacing(1.25),
            alignItems: 'center',
            padding: theme.spacing(1, 1.5),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        emptyState: {
            padding: theme.spacing(2),
            fontSize: 12,
            color: theme.palette.text.disabled,
            textAlign: 'center',
        },
        addButton: {
            width: '100%',
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 700,
            padding: theme.spacing(1, 1.5),
            borderTop: `1px dashed ${theme.palette.divider}`,
            borderRadius: 0,
        },
    })
);

export const AccessRuleTab: React.FC<ConfigureTabProps> = ({ form, values, taskflow }) => {
    const classes = useStyles();

    const otherTasks = taskflow.taskOrder
        .map(taskId => taskflow.tasks[taskId])
        .filter(otherTask => !!otherTask && otherTask.id !== values.id);

    const hasExclusionRules = values.access.excludeTaskIds.length > 0;

    const toggleExclusionRules = (checked: boolean) => {
        form.change('access', { ...values.access, excludeTaskIds: checked ? [otherTasks[0]?.id ?? ''] : [] });
    };

    return (
        <FormSectionCard
            title="Access Rule"
            description="Who should be excluded from being offered this task."
            color={PURPLE}
            bgColor="#f3e5f5"
            icon={LockOutlinedIcon}
        >
            <Box className={classes.toggleRow}>
                <Box>
                    <Typography className={classes.toggleTitle}>Exclude whoever triggered this Taskflow</Typography>
                    <Typography className={classes.toggleDesc}>
                        The system event that started this Taskflow on the matter — not another task's assignee.
                    </Typography>
                </Box>
                <Field name="access.excludeTrigger" type="checkbox" subscription={{ value: true }}>
                    {({ input }) => <Switch {...input} color="primary" checked={input.checked} />}
                </Field>
            </Box>

            <Box className={classes.toggleRow}>
                <Box>
                    <Typography className={classes.toggleTitle}>Exclude assignee of another task</Typography>
                    <Typography className={classes.toggleDesc}>
                        Excludes whoever's assigned to a specific other task in this Taskflow.
                    </Typography>
                </Box>
                <Switch color="primary" checked={hasExclusionRules} onChange={event => toggleExclusionRules(event.target.checked)} />
            </Box>

            {hasExclusionRules && (
                <Box className={classes.fieldRow}>
                    <Typography className={classes.label}>Exclude</Typography>
                    <Box className={classes.ruleTable}>
                        <Box className={classes.ruleHeadRow}>
                            <span>Task</span><span />
                        </Box>
                        <FieldArray name="access.excludeTaskIds">
                            {({ fields }) => (
                                <>
                                    {fields.map((name, index) => (
                                        <Box key={name} className={classes.ruleRow}>
                                            <Field name={name} subscription={{ value: true }}>
                                                {({ input }) => (
                                                    <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                        {otherTasks.map(otherTask => (
                                                            <MenuItem key={otherTask.id} value={otherTask.id}>{otherTask.id} · {otherTask.title}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            </Field>
                                            <IconButton size="small" onClick={() => fields.remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        className={classes.addButton}
                                        startIcon={<AddIcon fontSize="small" />}
                                        onClick={() => fields.push(otherTasks[0]?.id ?? '')}
                                    >
                                        Add exclusion
                                    </Button>
                                </>
                            )}
                        </FieldArray>
                    </Box>
                    <Typography className={classes.help}>
                        Works in either direction — pick a task earlier in the sequence, or one that hasn't run yet.
                    </Typography>
                </Box>
            )}
        </FormSectionCard>
    );
};
